import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { bind } from 'bind-event-listener';
import { useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
// Subpath import — `@atlaskit/flag`'s root barrel is on the prohibited-barrel ratcheting list.
import { FlagsProvider } from '@atlaskit/flag/flag-provider';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronLeftIcon from '@atlaskit/icon/core/chevron-left';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { Box, Flex, Pressable } from '@atlaskit/primitives/compiled';
// Type-only subpath import: `OptionType` is erased at build, and `/types` is not the prohibited
// root barrel, so this is safe for both the ratchet and the gemini-vr bundler.
import { type OptionType } from '@atlaskit/select/types';
import { token } from '@atlaskit/tokens';
import { rankByRecommendations } from '@atlassian/search-client/rank-by-recommendations';

import { universalTypeFilterId } from '../../../../common/constants/filters/types';
import { isNounKey, NounKeys } from '../../../../common/constants/nouns';
import { PrimaryProductKeys } from '../../../../common/constants/primary-product-keys';
import { ProductKeys, type PrimaryProductKey } from '../../../../common/constants/product-keys';
import { useProductConfigs } from '../../../../common/constants/products';
import {
	type ThirdPartyConfigsBootstrap,
	type ThirdPartyConfigsWithAPIResponse,
} from '../../../../common/constants/schemas/3p-config';
import type { TriggerComponentProps } from '../../../../common/types';
import { isWebCrawlerFanoutEnabled } from '../../../../common/ui/filters/app-filter-sidebar/app-filters/utils';
// PopupSelect is consumed via the local select-filter wrapper rather than the `@atlaskit/select`
// root barrel: the root barrel is on the prohibited-barrel ratchet, and the `/popup-select` subpath
// resolves to `undefined` under gemini-vr's atlaspack bundler.
import { AppFilterPopupSelect } from '../../../../common/ui/filters/select-filter';
import { Control } from '../../../../common/ui/filters/select-filter/control';
import { MenuListWithoutClearFilterOptionLabel } from '../../../../common/ui/filters/select-filter/menu-list';
import { OAuthWarningWrapper } from '../../../../common/ui/oauth-warning-wrapper';
import { messages as oAuthMessages } from '../../../../common/ui/oauth-warning-wrapper/messages';
import { ProductOrNounIcon } from '../../../../common/ui/product-icon';
import {
	type AppOption,
	buildNounOptions,
	buildProductOptions,
	getWebCrawlerDatasources,
	splitByAuthStatus,
} from '../../../../common/utils/app-filter-options';
import { isProductConnected, isProductSmartlink } from '../../../../common/utils/connectors';
import {
	type HorizontalAppFilterScrollDirection,
	onHorizontalAppFilterScroll,
} from '../../../../common/utils/events/horizontal-app-filter-scroll';
import { OAuthSource } from '../../../../common/utils/events/oauth-events';
import { onAppFilterShowMoreClicked } from '../../../../common/utils/events/on-app-filter-show-more-clicked';
import { onAppFilterOptionClicked } from '../../../../common/utils/events/product-option-clicked';
import {
	onSearchPageFiltersViewed,
	type PixelVisibleApp,
} from '../../../../common/utils/events/search-page-filters-viewed';
import {
	buildKnownAppKeySet,
	getVisibleAppAuthStatus,
	parseAppKeyFromTestId,
} from '../../../../common/utils/events/visible-apps-dom';
import { filterProductsForNouns } from '../../../../common/utils/filter-products-for-nouns';
import { getShouldShowNouns } from '../../../../common/utils/get-should-show-nouns';
import { getWorkspaceNameFromProduct } from '../../../../common/utils/get-workspace-name-from-product';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../../../controllers/analytics';
import { useIsUserOAuthed, useOAuthHandlers } from '../../../../controllers/oauth';
import { useRightPanelRecommendations } from '../../../../controllers/recommened3pApps';
import {
	useBootstrap,
	useCurrentUserId,
	usePrimaryProduct,
} from '../../../../controllers/store/bootstrap';
import {
	useFilters,
	useSearchActions,
	useSelectedDatasource,
	useSelectedProduct,
} from '../../../../controllers/store/search';

import { getScrollAmount as getScrollAmountFromLayout } from './get-scroll-amount';

const arePixelVisibleAppArraysEqual = (a: PixelVisibleApp[], b: PixelVisibleApp[]): boolean =>
	a.length === b.length &&
	a.every(
		(value, index) =>
			value.appKey === b[index].appKey && value.isAuthenticated === b[index].isAuthenticated,
	);

export const getPixelVisibleApps = (
	scrollEl: HTMLElement,
	thirdPartyConfigs?: ThirdPartyConfigsBootstrap,
): PixelVisibleApp[] => {
	const containerRect = scrollEl.getBoundingClientRect();
	const containerLeft = scrollEl.scrollLeft;
	const containerRight = containerLeft + scrollEl.clientWidth;
	// Identity is read from `data-testid` (the same `filter-<key>` contract the
	// non-lite rail uses, and the one pollinator tests depend on); auth is derived
	// from state via the shared `isProductConnected` helper. See `visible-apps-dom`.
	// Known-app keys are built from the static set + runtime `thirdPartyConfigs`,
	// matching the rail so a 3P key present in configs but not in the static list
	// isn't silently dropped from the Lite payload.
	const knownAppKeys = buildKnownAppKeySet(thirdPartyConfigs);
	const pills = Array.from(scrollEl.querySelectorAll<HTMLElement>('[data-testid]'));
	// Guard against a descendant element re-emitting an already-counted app key
	// (mirrors the rail reader); first ≥50%-visible pill per key wins.
	const seen = new Set<string>();

	return pills.reduce<PixelVisibleApp[]>((result, pill) => {
		const testId = pill.getAttribute('data-testid');
		const key = testId ? parseAppKeyFromTestId(testId, knownAppKeys) : undefined;
		const pillRect = pill.getBoundingClientRect();
		const pillWidth = pillRect.width;
		if (!key || pillWidth === 0 || seen.has(key)) {
			return result;
		}

		const pillLeft = pillRect.left - containerRect.left + scrollEl.scrollLeft;
		const pillRight = pillLeft + pillWidth;
		const overlapLeft = Math.max(pillLeft, containerLeft);
		const overlapRight = Math.min(pillRight, containerRight);
		const overlap = Math.max(0, overlapRight - overlapLeft);

		if (overlap / pillWidth < 0.5) {
			return result;
		}

		seen.add(key);
		return [
			...result,
			{ appKey: key, isAuthenticated: getVisibleAppAuthStatus(key, thirdPartyConfigs) },
		];
	}, []);
};

type AppPillLayout = {
	firstConnectedBatchSize: number;
	unauthenticated3pSlots: number;
	secondConnectedBatchSize: number;
};

// 6 connected 1P/3P apps then 3 top recommended unconnected 3P apps.
// Any remaining apps overflow to the "More apps" dropdown.
const DEFAULT_APP_PILL_LAYOUT: AppPillLayout = {
	firstConnectedBatchSize: 6,
	unauthenticated3pSlots: 3,
	secondConnectedBatchSize: 0,
};

/**
 * Applies an {@link AppPillLayout} to the given authenticated/unauthenticated app option lists,
 * producing the visible pill order and the overflow remainder.
 *
 * Pure function (no React/feature-gate access) so it can be tested and reused independently.
 */
export const applyAppPillLayout = ({
	layout,
	authenticatedOptions,
	rankedUnauthenticated3p,
}: {
	layout: AppPillLayout;
	authenticatedOptions: AppOption[];
	rankedUnauthenticated3p: AppOption[];
}): { visibleOptions: AppOption[]; overflowOptions: AppOption[] } => {
	const { firstConnectedBatchSize, unauthenticated3pSlots, secondConnectedBatchSize } = layout;

	const firstConnectedBatch = authenticatedOptions.slice(0, firstConnectedBatchSize);
	const visibleUnauthenticated3p = rankedUnauthenticated3p.slice(0, unauthenticated3pSlots);
	const secondConnectedBatch = authenticatedOptions.slice(
		firstConnectedBatchSize,
		firstConnectedBatchSize + secondConnectedBatchSize,
	);
	const visibleOptions = [
		...firstConnectedBatch,
		...visibleUnauthenticated3p,
		...secondConnectedBatch,
	];

	const overflowAuthenticated = authenticatedOptions.slice(
		firstConnectedBatchSize + secondConnectedBatchSize,
	);
	const overflowUnauthenticated3p = rankedUnauthenticated3p.slice(unauthenticated3pSlots);
	const overflowOptions = [...overflowAuthenticated, ...overflowUnauthenticated3p];

	return { visibleOptions, overflowOptions };
};

/**
 * Ranking for connected apps in the Lite-mode app filter pill bar.
 *
 * See: https://atlassian.slack.com/archives/C0AM7ADEAH3/p1780021272236159
 */
export const LITE_CONNECTED_APP_RANKING: ReadonlyArray<ProductKeys | NounKeys> = [
	ProductKeys.Jira,
	ProductKeys.Confluence,
	ProductKeys.GoogleDrive,
	ProductKeys.Loom,
	ProductKeys.Sharepoint,
	NounKeys.project,
	NounKeys.goal,
	ProductKeys.Slack,
	ProductKeys.Figma,
	ProductKeys.Github,
	ProductKeys.Miro,
	ProductKeys.Salesforce,
	ProductKeys.Bitbucket,
];

/**
 * Confluence-specific Lite connected app ranking.
 *
 * Static popularity-based order. The first 15 entries are the popularity-ranked
 * apps; `project`, `goal`, and Salesforce are intentionally kept at the end (they
 * are not part of the static popularity order but are retained so they keep a
 * defined position ahead of any unranked app).
 */
export const CONFLUENCE_LITE_CONNECTED_APP_RANKING: ReadonlyArray<ProductKeys | NounKeys> = [
	ProductKeys.Confluence,
	ProductKeys.Jira,
	ProductKeys.GoogleDrive,
	ProductKeys.Sharepoint,
	ProductKeys.Slack,
	ProductKeys.Github,
	ProductKeys.JiraServiceManagement,
	ProductKeys.Figma,
	ProductKeys.Bitbucket,
	ProductKeys.Assets,
	ProductKeys.Loom,
	ProductKeys.Miro,
	ProductKeys.Teams,
	ProductKeys.OneDrive,
	NounKeys.project,
	NounKeys.goal,
	ProductKeys.Salesforce,
];

/**
 * Returns the effective Lite connected app ranking based on the host primary product.
 *
 * When the host is Confluence, the Confluence-specific popularity-based order
 * ({@link CONFLUENCE_LITE_CONNECTED_APP_RANKING}) is used. For every other host the
 * default {@link LITE_CONNECTED_APP_RANKING} is returned unchanged.
 *
 * Note: this function is only reached when the host has already opted into the Lite
 * experience. For Confluence, the `rovo_ui_refresh_in_confluence_search_page_exp`
 * experiment is evaluated upstream in
 * `confluence/next/packages/advanced-search/src/SearchPage.tsx` to decide whether
 * to render the Lite page at all — so by the time this code runs we are guaranteed
 * to be in the test cohort and do not need to re-check it here.
 */
export const getLiteConnectedAppRanking = (
	primaryProduct: PrimaryProductKey | undefined,
): ReadonlyArray<ProductKeys | NounKeys> => {
	if (primaryProduct !== PrimaryProductKeys.Confluence) {
		return LITE_CONNECTED_APP_RANKING;
	}

	return CONFLUENCE_LITE_CONNECTED_APP_RANKING;
};

export const rankConnectedAppsForLite = (
	authenticatedOptions: AppOption[],
	ranking: ReadonlyArray<ProductKeys | NounKeys> = LITE_CONNECTED_APP_RANKING,
): AppOption[] => {
	const rankIndex = new Map<string, number>(ranking.map((key, index) => [String(key), index]));

	const decorated = authenticatedOptions.map((option, originalIndex) => {
		const rank = rankIndex.get(String(option.value));
		return {
			option,
			rank: rank === undefined ? Number.POSITIVE_INFINITY : rank,
			originalIndex,
		};
	});

	decorated.sort((a, b) => {
		if (a.rank !== b.rank) {
			return a.rank - b.rank;
		}
		return a.originalIndex - b.originalIndex;
	});

	return decorated.map((d) => d.option);
};

/**
 * Context that tracks the set of app keys that have been "promoted" from the
 * "Show more apps" dialog into the visible app filter pill bar. Promoted apps
 * are rendered as the first pills in the bar (before the regular visible
 * options) and are removed from the overflow dropdown. State is in-memory only
 * and intentionally does not persist across page reloads.
 */
type PromotedAppsContextValue = {
	promotedAppKeys: string[];
	promoteApp: (key: string) => void;
	// The specific Atlassian Web Crawler website (datasource) most recently selected. Kept here
	// (rather than derived from the live selection) so the web crawler pill keeps showing that
	// website's name — and stays re-selectable as that website — even after another app filter is
	// selected. In-memory only; does not persist across reloads.
	promotedWebCrawlerDatasource: ThirdPartyConfigsWithAPIResponse | undefined;
};

const PromotedAppsContext = createContext<PromotedAppsContextValue>({
	promotedAppKeys: [],
	promoteApp: () => {},
	promotedWebCrawlerDatasource: undefined,
});

/**
 * Provider for the promoted apps state. Place this around `LiteAppFilterBar`
 * and `LiteShowAllAppsButton` so they share the same in-memory list of
 * apps that have been selected from the "Show more apps" dialog.
 */
export const PromotedAppsProvider = ({
	children,
}: {
	children: React.ReactNode;
}): React.JSX.Element => {
	const [promotedAppKeys, setPromotedAppKeys] = useState<string[]>([]);
	const [promotedWebCrawlerDatasource, setPromotedWebCrawlerDatasource] = useState<
		ThirdPartyConfigsWithAPIResponse | undefined
	>(undefined);
	const [selectedProduct] = useSelectedProduct();
	const [selectedDatasource] = useSelectedDatasource();

	const promoteApp = useCallback((key: string) => {
		setPromotedAppKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
	}, []);

	// Whenever a specific web crawler website is the active selection (chosen from the dialog or
	// restored from the URL), remember it so its pill keeps showing that website's name after the
	// user selects a different app filter. Functional update keeps the effect from re-running on
	// unrelated selection changes.
	useEffect(() => {
		if (selectedProduct === ProductKeys.AtlassianWebCrawler && selectedDatasource) {
			setPromotedWebCrawlerDatasource((prev) =>
				prev?.datasourceId === selectedDatasource.datasourceId ? prev : selectedDatasource,
			);
		}
	}, [selectedProduct, selectedDatasource]);

	const value = useMemo(
		() => ({ promotedAppKeys, promoteApp, promotedWebCrawlerDatasource }),
		[promotedAppKeys, promoteApp, promotedWebCrawlerDatasource],
	);

	return <PromotedAppsContext.Provider value={value}>{children}</PromotedAppsContext.Provider>;
};

const usePromotedApps = (): PromotedAppsContextValue => useContext(PromotedAppsContext);

const styles = cssMap({
	pill: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		paddingBlock: token('space.075'),
		paddingInline: token('space.150'),
		borderRadius: token('radius.medium'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: 'transparent',
		cursor: 'pointer',
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		color: token('color.text.subtle'),
		backgroundColor: token('color.background.neutral'),
		whiteSpace: 'nowrap',
		flexShrink: 0,
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered'),
			color: token('color.text.subtle'),
		},
	},
	pillSelected: {
		backgroundColor: token('color.background.selected'),
		color: token('color.text.selected'),
		borderColor: token('color.border.selected'),
		'&:hover': {
			backgroundColor: token('color.background.selected.hovered'),
			color: token('color.text.selected'),
		},
	},
	scrollContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.150'),
		overflowX: 'auto',
		scrollbarWidth: 'none',
		scrollBehavior: 'smooth',
	},
	outerContainer: {
		display: 'flex',
		alignItems: 'center',
		position: 'relative',
	},
	chevronButton: {
		display: 'flex',
	},
	chevronContainer: {
		position: 'absolute',
		top: '0',
		bottom: '0',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		zIndex: 1,
		paddingInline: token('space.025'),
	},
	chevronLeft: {
		left: '0',
	},
	chevronRight: {
		right: '0',
	},
	iconContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '16px',
		height: '16px',
		overflow: 'hidden',
		flexShrink: 0,
	},
	/**
	 * Truncates long app labels with "…" so the visible bar stays compact and predictable.
	 * 100px lets short names ("Jira", "Confluence", "Loom") render in full while clipping
	 * long names like "Jira Service Management" → "Jira Service…" and
	 * "Jira Product Discovery" → "Jira Product…". Applied only to pills in the lite header
	 * bar — the "More apps" dialog has more room and shows the full label.
	 */
	pillLabel: {
		maxWidth: '100px',
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
		display: 'block',
	},
});

/**
 * Builds the ordered list of visible and overflow app options for the Lite mode pill bar.
 * - Total of up to 9 visible pills, split per the {@link DEFAULT_APP_PILL_LAYOUT}.
 * - Unauthenticated 3P apps are ranked by recommended3pApps order.
 * - Remaining apps beyond the visible budget go to the "Show all apps" overflow dropdown.
 *
 * Uses shared util functions from app-filter-options for product/noun building and auth splitting,
 * and delegates final visible/overflow slicing to {@link applyAppPillLayout}.
 */
function useLiteAppOptions(): { visibleOptions: AppOption[]; overflowOptions: AppOption[] } {
	const [{ products: availableProducts, nouns: availableNouns, thirdPartyConfigs }] =
		useBootstrap();
	const [selectedProduct] = useSelectedProduct();
	const [selectedDatasource] = useSelectedDatasource();
	const [primaryProduct] = usePrimaryProduct();
	const userId = useCurrentUserId();
	const { recommended3pApps } = useRightPanelRecommendations({ userId, showRecommendations: true });
	const filters = useFilters();
	const { promotedAppKeys, promoteApp, promotedWebCrawlerDatasource } = usePromotedApps();

	const { formatMessage } = useIntl();
	const products = useProductConfigs();

	// Derive the currently selected noun type from the universal type filter.
	// When a noun like "project" or "goal" is selected, the store sets product=townsquare
	// and sets the type filter to that noun value.
	const selectedNounType = useMemo(() => {
		const typeFilter = filters[universalTypeFilterId];
		if (typeFilter && 'selectedOptions' in typeFilter) {
			const selected = typeFilter.selectedOptions.find((opt) => opt.isSelected);
			return selected?.value;
		}
		return undefined;
	}, [filters]);

	const result = useMemo(() => {
		// Always exclude Townsquare/Atlas (rendered as "Home") from the Lite app pill bar.
		// In the right rail, Atlas is hidden because it's classified as a "nested product" of
		// the `atlas` product category and the category's primary product key (`'atlas'`) is
		// not a real ProductKeys value, so the parent never renders. The Lite filter bar does
		// not implement that nested-categories logic, so we drop Atlas explicitly here to keep
		// behaviour consistent with the right rail.
		const productsWithoutAtlas = availableProducts.filter((key) => key !== ProductKeys.Atlas);

		const shouldShowNouns = getShouldShowNouns(availableNouns, productsWithoutAtlas);
		const filteredProducts = filterProductsForNouns(productsWithoutAtlas, shouldShowNouns);

		const productOptions = buildProductOptions({
			filteredProducts,
			products,
			selectedProduct,
			thirdPartyConfigs,
		});

		const nounOptions = shouldShowNouns
			? buildNounOptions({
					availableNouns,
					availableProducts: productsWithoutAtlas,
					selectedProduct,
					selectedNounType,
					formatMessage,
				})
			: [];

		const allOptions = [...productOptions, ...nounOptions];
		const { authenticatedOptions: authenticatedRaw, unauthenticatedOptions: unauthenticatedRaw } =
			splitByAuthStatus(allOptions, thirdPartyConfigs);

		// Surface the specific web crawler website's name on the pill instead of the generic
		// "Web pages" noun label — so the pill reads e.g. "Atlassian Developer". Prefer the live
		// selection, but fall back to the last-selected website (`promotedWebCrawlerDatasource`) so
		// the name persists after the user selects a different app filter. Only when no website has
		// been chosen at all is the generic noun label kept.
		const activeWebCrawlerDatasource =
			selectedProduct === ProductKeys.AtlassianWebCrawler && selectedDatasource
				? selectedDatasource
				: promotedWebCrawlerDatasource;
		const selectedWebCrawlerName = activeWebCrawlerDatasource
			? activeWebCrawlerDatasource.workspaceName || activeWebCrawlerDatasource.workspaceUrl
			: undefined;
		const relabelSelectedWebCrawler = (option: AppOption): AppOption =>
			selectedWebCrawlerName && option.value === ProductKeys.AtlassianWebCrawler
				? { ...option, label: selectedWebCrawlerName }
				: option;
		const authenticatedOptions = authenticatedRaw.map(relabelSelectedWebCrawler);
		const unauthenticatedOptions = unauthenticatedRaw.map(relabelSelectedWebCrawler);

		const rankedAuthenticatedOptions = rankConnectedAppsForLite(
			authenticatedOptions,
			getLiteConnectedAppRanking(primaryProduct),
		);

		// Rank unauthenticated 3P by recommendations order: walk the recommended list first
		// (preserving rank), then append any unauthenticated apps not in the recommendations list.
		const rankedUnauthenticated3p = rankByRecommendations({
			items: unauthenticatedOptions,
			recommendedIds: recommended3pApps,
			idOf: (o) => o.value as string,
		});

		const { visibleOptions, overflowOptions } = applyAppPillLayout({
			layout: DEFAULT_APP_PILL_LAYOUT,
			authenticatedOptions: rankedAuthenticatedOptions,
			rankedUnauthenticated3p,
		});

		// Apply "promoted" apps: any app that the user selected from the
		// "Show more apps" dialog is moved out of the overflow list and
		// rendered as a pill at the start of the visible options. Promoted
		// apps stay in the visible bar for the rest of the session, even if
		// this means rendering more than the regular max of 9 pills.
		//
		// Promoted apps are rendered in *reverse* selection order: the most
		// recently promoted app appears first. For example, if the user clicks
		// Google and then Confluence, the visible bar shows
		// [Confluence, Google, ...].
		if (promotedAppKeys.length === 0) {
			return { visibleOptions, overflowOptions };
		}

		const promotedSet = new Set<string>(promotedAppKeys);

		// Build the promoted pills in *reverse* promotion order so the most
		// recently selected app appears first. Look up the option from either
		// the visible or overflow lists so that promotion is idempotent and
		// works regardless of where the option currently sits.
		const allOptionsByKey = new Map<string, AppOption>();
		for (const opt of [...visibleOptions, ...overflowOptions]) {
			allOptionsByKey.set(String(opt.value), opt);
		}
		const promotedOptions = [...promotedAppKeys]
			.reverse()
			.map((key) => allOptionsByKey.get(key))
			.filter((o): o is AppOption => o !== undefined);

		const filteredVisibleOptions = visibleOptions.filter((o) => !promotedSet.has(String(o.value)));
		const filteredOverflowOptions = overflowOptions.filter(
			(o) => !promotedSet.has(String(o.value)),
		);

		return {
			visibleOptions: [...promotedOptions, ...filteredVisibleOptions],
			overflowOptions: filteredOverflowOptions,
		};
	}, [
		availableProducts,
		availableNouns,
		selectedProduct,
		selectedDatasource,
		selectedNounType,
		thirdPartyConfigs,
		products,
		formatMessage,
		recommended3pApps,
		promotedAppKeys,
		promotedWebCrawlerDatasource,
		primaryProduct,
	]);

	// Auto-promote any overflow option that is currently selected. This handles
	// the case where the page is loaded with a URL whose `selectedProduct` lives
	// inside the "Show more apps" overflow dialog: instead of leaving the app
	// hidden inside the dialog (with only the "Show all apps" trigger reflecting
	// the selection), we surface the selected app as the first visible pill.
	// Once promoted, the app stays in the visible bar for the rest of the
	// session even if the user later clicks a different pill — this matches the
	// existing in-memory promotion behaviour for apps clicked from the dialog.
	useEffect(() => {
		for (const option of result.overflowOptions) {
			if (option.isSelected && option.value !== undefined) {
				promoteApp(String(option.value));
			}
		}
	}, [result.overflowOptions, promoteApp]);

	return result;
}

const AppPill = ({
	option,
	onSelect,
	isDisabled,
}: {
	option: AppOption;
	onSelect: (value: ProductKeys | NounKeys | undefined) => void;
	isDisabled?: boolean;
}) => {
	const { formatMessage } = useIntl();
	const label = option.isUnauthenticated
		? formatMessage({
				id: 'search_page.lite_app_filter_bar.connect',
				defaultMessage: 'Connect',
				description:
					'Label for an unauthenticated 3P app pill prompting the user to connect it. The adjacent product icon identifies the app.',
			})
		: option.label;
	const ariaLabel = option.isUnauthenticated
		? formatMessage(
				{
					id: 'search_page.lite_app_filter_bar.connect_app_aria',
					defaultMessage: 'Connect {app}',
					description:
						'Accessible label for an unauthenticated 3P app pill. Visible label is "Connect"; the app name is added here for screen readers.',
				},
				{ app: option.label },
			)
		: undefined;

	return (
		<Pressable
			xcss={cx(styles.pill, option.isSelected && styles.pillSelected)}
			testId={`filter-${String(option.value)}`}
			onClick={() => {
				if (!isDisabled) {
					onSelect(option.value);
				}
			}}
			aria-pressed={option.isSelected}
			aria-current={option.isSelected}
			aria-label={ariaLabel}
		>
			<Flex alignItems="center" gap="space.075">
				<Box xcss={styles.iconContainer}>
					<ProductOrNounIcon
						label=""
						size="xxsmall"
						productOrNounKey={option.value}
						isSelected={option.isSelected}
					/>
				</Box>
				<Box xcss={styles.pillLabel}>{label}</Box>
				{option.isUnauthenticated && (
					<LinkExternalIcon label="" size="small" color={token('color.icon.subtle')} />
				)}
			</Flex>
		</Pressable>
	);
};

const AllPill = ({ isSelected, onSelect }: { isSelected: boolean; onSelect: () => void }) => {
	const { formatMessage } = useIntl();
	return (
		<Pressable
			xcss={cx(styles.pill, isSelected && styles.pillSelected)}
			testId="filter-all"
			onClick={onSelect}
			aria-pressed={isSelected}
			aria-current={isSelected}
		>
			{formatMessage({
				id: 'search_page.lite_app_filter_bar.all',
				defaultMessage: 'All',
				description: 'Label for the All filter pill that shows results from all apps',
			})}
		</Pressable>
	);
};

/**
 * Shared hook that returns a stable callback for selecting a product or noun filter.
 * Used by both LiteAppFilterBar (visible pills) and LiteShowAllAppsButton (overflow menu).
 *
// Mirrors right-panel app filter click instrumentation — see src/ui/search-page-relay/right-panel/index.tsx:219
 */
function useLiteFilterSelect(options?: {
	isInShowMore?: boolean;
	fireAnalytics?: boolean;
}): (
	value: ProductKeys | NounKeys | undefined,
	index?: number,
	datasource?: ThirdPartyConfigsWithAPIResponse,
) => void {
	const isInShowMore = options?.isInShowMore ?? false;
	const fireAnalytics = options?.fireAnalytics ?? true;
	const [selectedProduct] = useSelectedProduct();
	const [selectedDatasource] = useSelectedDatasource();
	const [{ thirdPartyConfigs }] = useBootstrap();
	const {
		selectProductFilter,
		unselectProductFilter,
		unsafe_setAtlasNouns,
		unsafe_setWebPageType,
	} = useSearchActions();
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();

	// useCommonAndPrivateAttributes returns a new object on every render. Holding the latest
	// values in a ref and reading from it inside the callback prevents the returned handler from
	// being recreated on every render, which would otherwise cause every AppPill child to
	// re-render unnecessarily.
	const attributesRef = useRef({ commonAttributes, nonPrivacySafeAttributes });
	useEffect(() => {
		attributesRef.current = { commonAttributes, nonPrivacySafeAttributes };
	}, [commonAttributes, nonPrivacySafeAttributes]);

	return useCallback(
		(
			value: ProductKeys | NounKeys | undefined,
			index?: number,
			datasource?: ThirdPartyConfigsWithAPIResponse,
		) => {
			// Mirrors right-panel app filter click instrumentation — see src/ui/search-page-relay/right-panel/index.tsx:219
			const productKeyForEvent: ProductKeys | undefined =
				value && !isNounKey(value) && value !== NounKeys.AtlassianWebCrawler
					? (value as ProductKeys)
					: undefined;
			if (fireAnalytics) {
				fireAnalyticsEvent(
					onAppFilterOptionClicked({
						commonAttributes: attributesRef.current.commonAttributes,
						nonPrivacySafeAttributes: attributesRef.current.nonPrivacySafeAttributes,
						productKey: productKeyForEvent,
						isConnected: productKeyForEvent
							? isProductConnected(productKeyForEvent, thirdPartyConfigs)
							: true,
						...(index !== undefined && { index }),
						isInShowMore: isInShowMore ?? false,
					}),
				);
			}

			// Datasource-aware selection for a fanned-out Atlassian Web Crawler website.
			// Selecting a specific website sets product=atlassian-web-crawler + that datasource,
			// matching how the standard right rail selects an individual web-crawler website
			// (see right-panel `onSelectProductWithAnalytics` → `selectProductFilter`).
			// Clicking the already-active website toggles it off, mirroring the deselect
			// behaviour of the non-fanned-out options below.
			if (datasource) {
				const isDatasourceAlreadySelected =
					value === selectedProduct && selectedDatasource?.datasourceId === datasource.datasourceId;
				if (isDatasourceAlreadySelected) {
					unselectProductFilter();
				} else {
					selectProductFilter(value as ProductKeys, datasource);
				}
				return;
			}

			if (!value || value === selectedProduct) {
				unselectProductFilter();
				return;
			}
			if (value === NounKeys.AtlassianWebCrawler) {
				unsafe_setWebPageType();
				return;
			}
			if (isNounKey(value)) {
				unsafe_setAtlasNouns(value);
				return;
			}
			selectProductFilter(value as ProductKeys);
		},
		[
			fireAnalytics,
			fireAnalyticsEvent,
			isInShowMore,
			selectProductFilter,
			selectedProduct,
			selectedDatasource,
			thirdPartyConfigs,
			unselectProductFilter,
			unsafe_setAtlasNouns,
			unsafe_setWebPageType,
		],
	);
}

/**
 * Horizontal app filter bar for Lite mode. Shows "All" pill + up to 9 product pills
 * in a single horizontally-scrollable row. Chevron buttons appear on hover to scroll
 * left/right. A "Show all apps" dropdown provides access to overflow apps.
 */
export const LiteAppFilterBar = (): React.JSX.Element => {
	const [{ thirdPartyConfigs }] = useBootstrap();
	const [selectedProduct] = useSelectedProduct();
	const isAllSelected = !selectedProduct;

	const { visibleOptions } = useLiteAppOptions();
	const handleSelect = useLiteFilterSelect();
	const { promotedWebCrawlerDatasource } = usePromotedApps();

	// Clicking the web crawler pill should (re)select the specific website it represents, not the
	// generic "web pages" aggregate — so route its selection through the remembered datasource.
	const handlePillSelect = useCallback(
		(value: ProductKeys | NounKeys | undefined, index: number) =>
			handleSelect(
				value,
				index,
				value === ProductKeys.AtlassianWebCrawler ? promotedWebCrawlerDatasource : undefined,
			),
		[handleSelect, promotedWebCrawlerDatasource],
	);

	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();

	// See note in useLiteFilterSelect — useCommonAndPrivateAttributes returns a new object on
	// every render, so we read the latest values from a ref to keep fireScrollEvent stable.
	const attributesRef = useRef({ commonAttributes, nonPrivacySafeAttributes });
	useEffect(() => {
		attributesRef.current = { commonAttributes, nonPrivacySafeAttributes };
	}, [commonAttributes, nonPrivacySafeAttributes]);

	const scrollRef = useRef<HTMLDivElement>(null);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);
	const [isHovering, setIsHovering] = useState(false);

	const lastFiredVisibleAppsRef = useRef<PixelVisibleApp[] | null>(null);
	const scrollSettleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const fireSearchPageFiltersViewedEvent = useCallback(() => {
		const el = scrollRef.current;
		if (!el) {
			return;
		}

		const pixelVisibleApps = getPixelVisibleApps(el, thirdPartyConfigs);
		if (
			lastFiredVisibleAppsRef.current !== null &&
			arePixelVisibleAppArraysEqual(lastFiredVisibleAppsRef.current, pixelVisibleApps)
		) {
			return;
		}

		lastFiredVisibleAppsRef.current = pixelVisibleApps;
		fireAnalyticsEvent(
			onSearchPageFiltersViewed({
				commonAttributes: attributesRef.current.commonAttributes,
				nonPrivacySafeAttributes: attributesRef.current.nonPrivacySafeAttributes,
				visibleApps: pixelVisibleApps,
			}),
		);
	}, [fireAnalyticsEvent, thirdPartyConfigs]);

	const fireSearchPageFiltersViewedEventRef = useRef(fireSearchPageFiltersViewedEvent);
	useEffect(() => {
		fireSearchPageFiltersViewedEventRef.current = fireSearchPageFiltersViewedEvent;
	}, [fireSearchPageFiltersViewedEvent]);

	// Debounce the initial fire so apps in the right rail have time to stabilise
	// before we snapshot which ones are pixel-visible.
	const scheduleSearchPageFiltersViewedEvent = useCallback(() => {
		return setTimeout(() => fireSearchPageFiltersViewedEventRef.current(), 250);
	}, []);
	const scheduleSearchPageFiltersViewedEventRef = useRef(scheduleSearchPageFiltersViewedEvent);
	useEffect(() => {
		scheduleSearchPageFiltersViewedEventRef.current = scheduleSearchPageFiltersViewedEvent;
	}, [scheduleSearchPageFiltersViewedEvent]);

	const scheduleSearchPageFiltersViewedEventAfterScroll = useCallback(() => {
		if (scrollSettleTimeoutRef.current !== null) {
			clearTimeout(scrollSettleTimeoutRef.current);
		}
		scrollSettleTimeoutRef.current = setTimeout(() => {
			scrollSettleTimeoutRef.current = null;
			fireSearchPageFiltersViewedEventRef.current();
		}, 350);
	}, []);

	useEffect(() => {
		const timeoutId = scheduleSearchPageFiltersViewedEventRef.current();
		return () => {
			clearTimeout(timeoutId);
			if (scrollSettleTimeoutRef.current !== null) {
				clearTimeout(scrollSettleTimeoutRef.current);
			}
		};
	}, []);

	const fireScrollEvent = useCallback(
		(direction: HorizontalAppFilterScrollDirection) => {
			const el = scrollRef.current;
			if (!el) {
				return;
			}
			fireAnalyticsEvent(
				onHorizontalAppFilterScroll({
					commonAttributes: attributesRef.current.commonAttributes,
					nonPrivacySafeAttributes: attributesRef.current.nonPrivacySafeAttributes,
					direction,
				}),
			);
		},
		[fireAnalyticsEvent],
	);

	const updateScrollState = useCallback(() => {
		const el = scrollRef.current;
		if (!el) {
			return;
		}
		setCanScrollLeft(el.scrollLeft > 0);
		setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
	}, []);

	useEffect(() => {
		const el = scrollRef.current;
		if (!el) {
			return;
		}
		const handleScroll = () => {
			updateScrollState();
			scheduleSearchPageFiltersViewedEventAfterScroll();
		};
		updateScrollState();
		const unbindScroll = bind(el, { type: 'scroll', listener: handleScroll });
		const resizeObserver = new ResizeObserver(updateScrollState);
		resizeObserver.observe(el);
		return () => {
			unbindScroll();
			resizeObserver.disconnect();
		};
	}, [scheduleSearchPageFiltersViewedEventAfterScroll, updateScrollState]);

	const computeScrollAmount = useCallback((direction: 'left' | 'right') => {
		const el = scrollRef.current;
		if (!el) {
			return 0;
		}

		const children = Array.from(el.children) as HTMLElement[];
		return getScrollAmountFromLayout(
			direction,
			{ scrollLeft: el.scrollLeft, clientWidth: el.clientWidth },
			children.map((child) => ({ offsetLeft: child.offsetLeft, offsetWidth: child.offsetWidth })),
		);
	}, []);

	const scrollLeft = useCallback(() => {
		const amount = computeScrollAmount('left');
		if (amount === 0) {
			return;
		}
		fireScrollEvent('left');
		scrollRef.current?.scrollBy({ left: -amount, behavior: 'smooth' });
	}, [computeScrollAmount, fireScrollEvent]);

	const scrollRight = useCallback(() => {
		const amount = computeScrollAmount('right');
		if (amount === 0) {
			return;
		}
		fireScrollEvent('right');
		scrollRef.current?.scrollBy({ left: amount, behavior: 'smooth' });
	}, [computeScrollAmount, fireScrollEvent]);

	return (
		<Box
			xcss={styles.outerContainer}
			onMouseEnter={() => setIsHovering(true)}
			onMouseLeave={() => setIsHovering(false)}
		>
			{isHovering && canScrollLeft && (
				<Box
					xcss={cx(styles.chevronContainer, styles.chevronLeft)}
					style={{
						background: `linear-gradient(to right, ${token('elevation.surface')} 28px, transparent 55px)`,
						paddingInline: `${token('space.0')} ${token('space.400')}`,
					}}
				>
					<Box xcss={styles.chevronButton}>
						<IconButton
							appearance="default"
							spacing="default"
							label="Scroll left"
							icon={ChevronLeftIcon}
							onClick={scrollLeft}
						/>
					</Box>
				</Box>
			)}

			<Box ref={scrollRef} xcss={styles.scrollContainer} testId="lite-app-filter-scroll-container">
				<AllPill isSelected={isAllSelected} onSelect={() => handleSelect(undefined, 0)} />
				{visibleOptions.map((option, idx) => {
					// 1-based index so the "All" pill remains at position 0 — matches the
					// position semantics of `index` on the right-panel app filter event.
					const pillIndex = idx + 1;
					return option.isUnauthenticated ? (
						<OAuthWarningWrapper
							key={option.value}
							product={option.value as ProductKeys}
							isSmartlink={isProductSmartlink({
								productKey: option.value,
								thirdPartyConfigs,
							})}
							useOAuthHandlers={useOAuthHandlers}
							useIsUserOAuthed={useIsUserOAuthed}
							oAuthSource={OAuthSource.searchPageFilter}
							thirdPartyConfigs={thirdPartyConfigs}
							skipFlagsProvider={false}
							onAppFilterClick={() => {
								fireAnalyticsEvent(
									onAppFilterOptionClicked({
										commonAttributes: attributesRef.current.commonAttributes,
										nonPrivacySafeAttributes: attributesRef.current.nonPrivacySafeAttributes,
										productKey: option.value as ProductKeys,
										isConnected: isProductConnected(option.value, thirdPartyConfigs),
										index: pillIndex,
										isInShowMore: false,
									}),
								);
							}}
							trigger={({ isDisabled }: TriggerComponentProps) => (
								<AppPill
									option={option}
									onSelect={(value) => handlePillSelect(value, pillIndex)}
									isDisabled={isDisabled}
								/>
							)}
						/>
					) : (
						<AppPill
							key={option.value}
							option={option}
							onSelect={(value) => handlePillSelect(value, pillIndex)}
						/>
					);
				})}
			</Box>

			{isHovering && canScrollRight && (
				<Box
					xcss={cx(styles.chevronContainer, styles.chevronRight)}
					style={{
						background: `linear-gradient(to left, ${token('elevation.surface')} 28px, transparent 55px)`,
						paddingInline: `${token('space.400')} ${token('space.0')}`,
					}}
				>
					<Box xcss={styles.chevronButton}>
						<IconButton
							appearance="default"
							spacing="default"
							label="Scroll right"
							icon={ChevronRightIcon}
							onClick={scrollRight}
						/>
					</Box>
				</Box>
			)}
		</Box>
	);
};

type LiteAppSelectOption = {
	value: string;
	label: string;
	isSelected: boolean;
	isUnauthenticated: boolean;
	// Set for fanned-out Atlassian Web Crawler website rows: `value` is a per-website composite id
	// (for PopupSelect identity), so `productKey` carries the real filter key (for the icon +
	// selection) and `datasource` the specific website to scope the selection to.
	productKey?: ProductKeys | NounKeys;
	datasource?: ThirdPartyConfigsWithAPIResponse;
};

const allAppsStyles = cssMap({
	optionRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: token('space.100'),
		width: '100%',
	},
	optionMain: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		overflow: 'hidden',
	},
	optionLabel: {
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
		font: token('font.body'),
		color: token('color.text'),
	},
	connectText: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.050'),
		color: token('color.link'),
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		whiteSpace: 'nowrap',
		flexShrink: 0,
	},
});

const LiteShowAllAppsButtonImpl = (): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();
	// useCommonAndPrivateAttributes returns a new object each render; hold the latest in a ref so
	// the memoized handleMenuOpen below stays stable (mirrors useLiteFilterSelect).
	const attributesRef = useRef({ commonAttributes, nonPrivacySafeAttributes });
	useEffect(() => {
		attributesRef.current = { commonAttributes, nonPrivacySafeAttributes };
	}, [commonAttributes, nonPrivacySafeAttributes]);
	const [{ thirdPartyConfigs }] = useBootstrap();
	const products = useProductConfigs();

	const { visibleOptions, overflowOptions } = useLiteAppOptions();
	// `isInShowMore` stamps the option-clicked analytics as coming from the dialog,
	// preserving the event shape the previous "All apps" popup fired.
	const handleSelect = useLiteFilterSelect({ isInShowMore: true });
	const { promoteApp } = usePromotedApps();
	const [selectedProduct] = useSelectedProduct();
	const [selectedDatasource] = useSelectedDatasource();

	const appOptions = useMemo(
		() => [...visibleOptions, ...overflowOptions],
		[visibleOptions, overflowOptions],
	);

	// Atlassian Web Crawler website datasources to fan out into individual dialog rows.
	// Gated (via the shared `isWebCrawlerFanoutEnabled`) behind the same
	// `webcrawler-fanout-ui-sidebar-enabled` feature gate the standard right rail uses, so the
	// Lite "More apps" dialog lists individual websites like the standard right rail does.
	const webCrawlerDatasources = useMemo(
		() =>
			isWebCrawlerFanoutEnabled(thirdPartyConfigs)
				? getWebCrawlerDatasources(thirdPartyConfigs)
				: [],
		[thirdPartyConfigs],
	);

	const selectOptions = useMemo<LiteAppSelectOption[]>(() => {
		const otherOptions: LiteAppSelectOption[] = [];
		const webCrawlerWebsites: LiteAppSelectOption[] = [];

		for (const option of appOptions) {
			// Fan the Atlassian Web Crawler out into one option per website datasource (e.g.
			// "Atlassian Developer") so the dialog lists individual websites, matching the standard
			// right rail. Each row gets a unique `value` for PopupSelect identity and carries its
			// `datasource`/`productKey` so selection + the icon resolve correctly.
			if (option.value === ProductKeys.AtlassianWebCrawler && webCrawlerDatasources.length > 0) {
				for (const datasource of webCrawlerDatasources) {
					webCrawlerWebsites.push({
						value: `${option.value}-${datasource.datasourceId}`,
						label: datasource.workspaceName || datasource.workspaceUrl || option.label,
						isSelected:
							selectedProduct === ProductKeys.AtlassianWebCrawler &&
							selectedDatasource?.datasourceId === datasource.datasourceId,
						isUnauthenticated: Boolean(option.isUnauthenticated),
						productKey: option.value,
						datasource,
					});
				}
			} else {
				otherOptions.push({
					value: String(option.value),
					label: option.label,
					isSelected: option.isSelected,
					isUnauthenticated: Boolean(option.isUnauthenticated),
				});
			}
		}

		// Selecting one website promotes the single web crawler entry to the front, which would
		// otherwise drag every fanned-out website to the top. Instead surface only the selected
		// website at the top and keep the rest grouped in the web crawler's natural position
		// (further down the list), so only the chosen website "propagates" up.
		const selectedWebsiteIndex = webCrawlerWebsites.findIndex((option) => option.isSelected);
		if (selectedWebsiteIndex === -1) {
			return [...otherOptions, ...webCrawlerWebsites];
		}
		const [selectedWebsite] = webCrawlerWebsites.splice(selectedWebsiteIndex, 1);
		return [selectedWebsite, ...otherOptions, ...webCrawlerWebsites];
	}, [appOptions, webCrawlerDatasources, selectedProduct, selectedDatasource]);

	const hasSelectedOverflow = overflowOptions.some((option) => option.isSelected);

	const [connectProduct, setConnectProduct] = useState<ProductKeys | undefined>(undefined);
	const { outboundAuthUrl } = useIsUserOAuthed(connectProduct);
	const connectProductName = connectProduct
		? getWorkspaceNameFromProduct(connectProduct, thirdPartyConfigs) ||
			String(products[connectProduct]?.displayName ?? '')
		: '';
	const { onAuthorize } = useOAuthHandlers({
		product: connectProduct,
		successFlagTitle: formatMessage(oAuthMessages.authorizationSuccess, {
			product: connectProductName,
		}),
		failFlagTitle: formatMessage(oAuthMessages.authorizationFail, { product: connectProductName }),
		outboundAuthUrl: outboundAuthUrl || '',
	});
	// `onAuthorize` is a fresh reference each render; keep the latest in a ref (assigned in an
	// effect to keep render pure) so the connect effect below only re-runs when the
	// selected-to-connect app or its resolved auth URL changes. This effect is declared first so
	// the ref is updated before the connect effect reads it on the same commit.
	const onAuthorizeRef = useRef(onAuthorize);
	useEffect(() => {
		onAuthorizeRef.current = onAuthorize;
	}, [onAuthorize]);

	useEffect(() => {
		if (connectProduct && outboundAuthUrl) {
			onAuthorizeRef.current(OAuthSource.searchPageFilter);
			setConnectProduct(undefined);
		}
	}, [connectProduct, outboundAuthUrl]);

	const formatOptionLabel = useCallback(
		(option: OptionType) => {
			const opt = option as LiteAppSelectOption;
			// For fanned-out web-crawler rows the `value` is a composite id, so resolve the icon
			// from the real `productKey` when present.
			const optionValue = (opt.productKey ?? opt.value) as ProductKeys | NounKeys | undefined;
			return (
				<Box xcss={allAppsStyles.optionRow}>
					<Box xcss={allAppsStyles.optionMain}>
						<ProductOrNounIcon label="" size="small" productOrNounKey={optionValue} />
						<Box xcss={allAppsStyles.optionLabel}>{opt.label}</Box>
					</Box>
					{opt.isUnauthenticated && (
						<Box xcss={allAppsStyles.connectText}>
							{formatMessage({
								id: 'search_page.lite_app_filter_bar.connect',
								defaultMessage: 'Connect',
								description:
									'Label next to an unconnected third-party app in the More apps dropdown; selecting it starts the connect flow.',
							})}
						</Box>
					)}
				</Box>
			);
		},
		[formatMessage],
	);

	const onChange = useCallback(
		(value: OptionType | readonly OptionType[] | null) => {
			const selected = (Array.isArray(value) ? value[0] : value) as LiteAppSelectOption | null;
			if (!selected) {
				return;
			}
			// For fanned-out web-crawler websites, `value` is a composite id — the real filter key
			// is `productKey`, and `datasource` scopes the selection to that specific website.
			const productKey = (selected.productKey ?? selected.value) as ProductKeys | NounKeys;
			// Unconnected 3P app: start the OAuth connect flow instead of applying a filter.
			if (selected.isUnauthenticated) {
				setConnectProduct(productKey as ProductKeys);
				return;
			}
			const index = appOptions.findIndex((option) => String(option.value) === String(productKey));
			// Promote the app so it renders as a pill at the start of the visible bar,
			// matching the previous "All apps" dialog behaviour.
			promoteApp(String(productKey));
			handleSelect(productKey, index === -1 ? undefined : index, selected.datasource);
		},
		[appOptions, promoteApp, handleSelect],
	);

	const handleMenuOpen = useCallback(() => {
		fireAnalyticsEvent(
			onAppFilterShowMoreClicked({
				commonAttributes: attributesRef.current.commonAttributes,
				nonPrivacySafeAttributes: attributesRef.current.nonPrivacySafeAttributes,
				isConnected: true,
				section: 'filterResults',
			}),
		);
	}, [fireAnalyticsEvent]);

	const isOptionSelected = useCallback((option: LiteAppSelectOption) => option.isSelected, []);

	if (overflowOptions.length === 0) {
		return null;
	}

	const moreAppsLabel = formatMessage({
		id: 'search_page.lite_app_filter_bar.more_apps',
		defaultMessage: 'More apps',
		description: 'Label for the dropdown that shows the full list of app filters',
	});

	return (
		<AppFilterPopupSelect<LiteAppSelectOption>
			label={moreAppsLabel}
			placeholder={formatMessage({
				id: 'search_page.lite_app_filter_bar.find_apps',
				defaultMessage: 'Find apps',
				description: 'Placeholder for the search input in the More apps filter dropdown',
			})}
			options={selectOptions}
			isOptionSelected={isOptionSelected}
			onChange={onChange}
			onMenuOpen={handleMenuOpen}
			formatOptionLabel={formatOptionLabel}
			isSearchable
			searchThreshold={-1}
			blurInputOnSelect={false}
			isClearable={false}
			closeMenuOnSelect
			shouldCloseMenuOnTab={false}
			// Match the previous dialog's footprint: ~259px wide, and tall enough to show the
			// 6 connected + 3 unauthenticated apps (max 9); any remaining apps scroll.
			minMenuWidth={259}
			maxMenuWidth="259px"
			maxMenuHeight={336}
			menuPlacement="bottom"
			components={{ Control, MenuList: MenuListWithoutClearFilterOptionLabel }}
			popperProps={{
				strategy: 'fixed',
				modifiers: [
					{ name: 'offset', options: { offset: [0, 8] } },
					{ name: 'preventOverflow', enabled: false },
				],
			}}
			// The trigger stays the bespoke "More apps" button (no visual change); the
			// selected app is surfaced as a promoted pill in the bar and marked in the menu
			// via each option's `isSelected`.
			target={({ isOpen, ...targetProps }) => (
				<Button
					{...targetProps}
					appearance="default"
					isSelected={hasSelectedOverflow || isOpen}
					iconAfter={(iconProps) => <ChevronDownIcon {...iconProps} size="small" />}
				>
					{moreAppsLabel}
				</Button>
			)}
		/>
	);
};

// `FlagsProvider` is required by `useOAuthHandlers` (it calls `useFlags`, which throws without a
// provider) used for the 3P connect flow. `OAuthWarningWrapper` wraps itself the same way.
export const LiteShowAllAppsButton = (): React.JSX.Element => (
	<FlagsProvider>
		<LiteShowAllAppsButtonImpl />
	</FlagsProvider>
);
